import { X, Lock } from 'lucide-react';

interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyPolicy = ({ isOpen, onClose }: PrivacyPolicyProps) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — 2026 theme */}
        <div className="text-white px-6 py-4 border-b border-[#0f172a]/20 flex justify-between items-center sticky top-0 z-10" style={{ backgroundColor: 'var(--smb-primary, #1e3a5f)' }}>
          <div className="flex items-center gap-2">
            <Lock size={24} />
            <h2 
              id="privacy-title"
              className="font-bold text-xl"
            >
              Privacy Policy
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            aria-label="Close Privacy Policy"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 text-sm text-gray-700">
          {/* Introduction */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">1. Introduction</h3>
            <p className="mb-2">
              This Privacy Policy explains how <strong>VerifyPH</strong> ("we", "our", "the platform") 
              collects, uses, and protects your personal information in compliance with the{' '}
              <strong>Data Privacy Act of 2012 (Republic Act 10173)</strong> of the Philippines.
            </p>
            <p>
              VerifyPH is a citizen-powered transparency platform that enables citizens to document 
              and share observations related to government-funded projects and public service delivery 
              through evidence-based reporting.
            </p>
          </section>

          {/* Data Collection */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">2. Information We Collect</h3>
            <p className="mb-2">We collect the following types of information:</p>
            
            <h4 className="font-semibold text-gray-800 mt-3 mb-1">2.1. Account Information</h4>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Display name (as provided by you)</li>
              <li>Email address (if using Google Sign-In)</li>
              <li>Profile photo (if using Google Sign-In)</li>
              <li>Firebase Authentication user ID</li>
            </ul>

            <h4 className="font-semibold text-gray-800 mt-3 mb-1">2.2. Citizen Report Information</h4>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Report category (Anti-Epal Watch, Project Delay, Ghost Project, Service Failure, Compliance Gap)</li>
              <li>Politician name (for Anti-Epal Watch category, if applicable)</li>
              <li>Location of reported observation</li>
              <li>Description of observation</li>
              <li>Photographic evidence</li>
              <li>Category-specific fields (project type, funding source, violation type for Anti-Epal Watch; project name, expected timeline for project-related categories; etc.)</li>
              <li>Geolocation data (if provided)</li>
              <li>Submission timestamp</li>
            </ul>

            <h4 className="font-semibold text-gray-800 mt-3 mb-1">2.3. Usage Information</h4>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Comments and interactions</li>
              <li>Votes (valid/abuse flags)</li>
              <li>Platform usage patterns</li>
            </ul>
          </section>

          {/* How We Use Data */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">3. How We Use Your Information</h3>
            <p className="mb-2">We use your information to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Provide and operate the citizen reporting platform</li>
              <li>Moderate and review submitted citizen reports</li>
              <li>Display reports and user contributions for transparency</li>
              <li>Support policy compliance tracking and research</li>
              <li>Prevent abuse and ensure platform security</li>
              <li>Respond to user inquiries and support requests</li>
              <li>Generate anonymized analytics and statistics</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">4. Data Sharing and Disclosure</h3>
            <p className="mb-2">We may share your information in the following circumstances:</p>
            
            <h4 className="font-semibold text-gray-800 mt-3 mb-1">4.1. Public Reports</h4>
            <p className="ml-4 mb-2">
              Approved citizen reports are publicly visible and include:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-8">
              <li>Your display name (as provided)</li>
              <li>Report content (category, location, description, photos, category-specific fields)</li>
              <li>Report category and associated metadata</li>
            </ul>

            <h4 className="font-semibold text-gray-800 mt-3 mb-1">4.2. Research and Policy Support</h4>
            <p className="ml-4 mb-2">
              We may share anonymized or aggregated data for:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-8">
              <li>Policy compliance tracking and research</li>
              <li>Transparency and accountability initiatives</li>
              <li>Supporting evidence-based governance</li>
            </ul>

            <h4 className="font-semibold text-gray-800 mt-3 mb-1">4.3. Service Providers</h4>
            <p className="ml-4">
              We use Firebase (Google) for authentication and data storage. Your data is subject to 
              Firebase's privacy policies and security measures.
            </p>

            <h4 className="font-semibold text-gray-800 mt-3 mb-1">4.4. Legal Requirements</h4>
            <p className="ml-4">
              We may disclose information if required by law, court order, or to protect our rights 
              and the safety of users.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">5. Data Security</h3>
            <p className="mb-2">We implement security measures to protect your information:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Firebase Authentication for secure user accounts</li>
              <li>Firestore database with security rules</li>
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular security reviews and updates</li>
            </ul>
            <p className="mt-2">
              However, no method of transmission over the internet is 100% secure. While we strive 
              to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          {/* User Rights */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">6. Your Rights (Data Privacy Act)</h3>
            <p className="mb-2">Under the Data Privacy Act of 2012, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Objection:</strong> Object to processing of your data</li>
              <li><strong>Data Portability:</strong> Request a copy of your data</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, please contact the platform administrators through the 
              appropriate channels.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">7. Data Retention</h3>
            <p>
              We retain your personal data for as long as necessary to provide the service and 
              comply with legal obligations. Public reports may be retained for historical and 
              transparency purposes. You may request deletion of your account and associated data 
              at any time.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">8. Cookies and Tracking</h3>
            <p>
              We use Firebase Authentication which may use cookies and similar technologies for 
              authentication and session management. We do not use third-party tracking cookies 
              or advertising trackers.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">9. Children's Privacy</h3>
            <p>
              This platform is not intended for users under 18 years of age. We do not knowingly 
              collect personal information from children. If you believe we have collected 
              information from a child, please contact us immediately.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">10. Third-Party Links</h3>
            <p>
              Our platform may contain links to external websites (such as DILG official website). 
              We are not responsible for the privacy practices of these external sites. Please 
              review their privacy policies.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">11. Changes to This Privacy Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. We will notify users of 
              significant changes. Continued use of the platform after changes constitutes 
              acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-gray-900 text-base mb-2">12. Contact Information</h3>
            <p className="mb-2">
              For questions, concerns, or to exercise your data privacy rights, please contact 
              the platform administrators through the appropriate channels.
            </p>
            <p className="text-xs text-gray-600">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};
